import React, { useState, useEffect } from 'react';
import { ArrowRight, CheckCircle, Phone, Clock, Award } from 'lucide-react';

const ResultPageComponent = ({ 
  phoneNumber, 
  token, 
  customResultUrl, 
  mainPageUrl,
  formData,
  onBackToMain,
  onLogout 
}) => {
  const [iframeLoading, setIframeLoading] = useState(true);
  const [resultData, setResultData] = useState(null);
  const [processing, setProcessing] = useState(false);

  // Handle iframe communication
  useEffect(() => {
    const handleMessage = (event) => {
      try {
        if (event.data.type === 'BACK_TO_MAIN') {
          handleBackToMain();
        } else if (event.data.type === 'LOGOUT') {
          handleLogout();
        } else if (event.data.type === 'RESULT_READY') {
          // Result page has loaded and is ready for data
          sendResultDataToIframe();
        } else if (event.data.type === 'DOWNLOAD_RESULTS') {
          handleDownloadResults();
        } else if (event.data.type === 'SHARE_RESULTS') {
          handleShareResults(event.data.platform);
        }
      } catch (error) {
        console.warn('Invalid iframe message:', error);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [phoneNumber, token, formData]); // eslint-disable-line react-hooks/exhaustive-deps

  // Send result data to iframe when loaded
  const sendResultDataToIframe = () => {
    if (!customResultUrl) return;
    
    try {
      const iframe = document.getElementById('custom-result-iframe');
      if (iframe && iframe.contentWindow) {
        const resultInfo = {
          type: 'RESULT_DATA',
          phoneNumber: phoneNumber,
          token: token,
          success: true,
          timestamp: Date.now(),
          submittedAt: new Date().toLocaleString('vi-VN'),
          
          // Form data from submission
          formData: formData || {},
          
          // Additional context data
          resultId: `result_${Date.now()}`,
          sessionId: `session_${Date.now()}`,
          
          // Processing info
          processingTime: '2.3s',
          formScore: 'A+'
        };
        
        iframe.contentWindow.postMessage(resultInfo, '*');
        setResultData(resultInfo);
      }
    } catch (error) {
      console.warn('Could not send data to iframe:', error);
    }
  };

  // Handle iframe load event
  const handleIframeLoad = () => {
    setIframeLoading(false);
    // Small delay to ensure iframe is fully rendered
    setTimeout(() => {
      sendResultDataToIframe();
    }, 100);
  };

  // Handle back to main
  const handleBackToMain = () => {
    setProcessing(true);
    
    try {
      if (onBackToMain) {
        onBackToMain();
      } else {
        // Default behavior: redirect to main page
        const params = new URLSearchParams({
          token,
          mainUrl: encodeURIComponent(mainPageUrl),
          resultUrl: encodeURIComponent(customResultUrl)
        });
        
        window.history.replaceState({}, '', `?${params.toString()}`);
        
        // Trigger smooth page change in parent component
        if (window.smoothPageTransition) {
          window.smoothPageTransition('main', 300);
        } else if (window.setCurrentPage) {
          window.setCurrentPage('main');
        }
      }
    } catch (error) {
      console.error('Back to main error:', error);
    } finally {
      setProcessing(false);
    }
  };

  // Handle logout
  const handleLogout = () => {
    if (window.confirm('Bạn có chắc chắn muốn đăng xuất?')) {
      if (onLogout) {
        onLogout();
      }
    }
  };

  // Handle download results
  const handleDownloadResults = () => {
    try {
      const data = {
        phoneNumber,
        submittedAt: new Date().toISOString(),
        formData: formData || {},
                  resultId: `result_${Date.now()}`
      };
      
      const blob = new Blob([JSON.stringify(data, null, 2)], {
        type: 'application/json'
      });
      
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `form-results-${phoneNumber}-${Date.now()}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      // Show success message
      alert('Kết quả đã được tải xuống thành công!');
    } catch (error) {
      console.error('Download error:', error);
      alert('Có lỗi xảy ra khi tải xuống. Vui lòng thử lại.');
    }
  };

  // Handle share results
  const handleShareResults = (platform) => {
    const shareUrl = window.location.href;
    const shareText = `Tôi vừa hoàn thành form điện tử! 📝✅`;
    
    try {
      if (platform === 'copy') {
        navigator.clipboard.writeText(shareUrl);
        alert('Link đã được copy vào clipboard!');
      } else if (platform === 'facebook') {
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`, '_blank');
      } else if (platform === 'twitter') {
        window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`, '_blank');
      }
    } catch (error) {
      console.error('Share error:', error);
    }
  };



  // Error state
  if (!customResultUrl) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-brand-50 to-brand-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-md p-8 text-center max-w-md">
          <h1 className="text-xl font-bold text-red-600 mb-4">Lỗi cấu hình</h1>
          <p className="text-gray-600 mb-4">Không tìm thấy URL của result page</p>
          <button
            onClick={handleBackToMain}
            className="bg-brand-500 text-white px-6 py-2 rounded-lg hover:bg-brand-600 mr-2"
          >
            Quay lại main page
          </button>
          <button
            onClick={handleLogout}
            className="bg-red-500 text-white px-6 py-2 rounded-lg hover:bg-red-600"
          >
            Đăng xuất
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-brand-50 to-brand-100 p-4">
      <div className="max-w-full mx-auto h-screen flex flex-col">
        {/* Header with result info and controls */}
        <div className="bg-white rounded-lg shadow-md p-4 mb-4 flex-shrink-0">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-brand-500 rounded-full flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-800">Kết Quả Form</h1>
                <div className="flex items-center gap-4 text-sm text-gray-600">
                  <div className="flex items-center gap-1">
                    <Phone className="w-4 h-4" />
                    <span>{phoneNumber}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    <span>{new Date().toLocaleString('vi-VN')}</span>
                  </div>
                  {resultData && (
                    <div className="flex items-center gap-1">
                      <Award className="w-4 h-4" />
                      <span>Điểm: A+</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              {/* Processing status */}
              {processing && (
                <div className="flex items-center gap-2 text-sm text-brand-600">
                  <div className="w-4 h-4 border-2 border-brand-600 border-t-transparent rounded-full animate-spin"></div>
                  <span>Đang xử lý...</span>
                </div>
              )}
              
              {/* Action buttons */}
              <button
                onClick={handleBackToMain}
                disabled={processing}
                className="px-4 py-2 bg-brand-500 text-white rounded-lg hover:bg-brand-600 text-sm font-medium transition-colors duration-200 disabled:opacity-50 flex items-center gap-2"
              >
                <ArrowRight className="w-4 h-4" />
                Quay lại trang chính
              </button>
              
              <button
                onClick={handleLogout}
                disabled={processing}
                className="px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg text-sm font-medium transition-colors duration-200 disabled:opacity-50"
              >
                Đăng xuất
              </button>
            </div>
          </div>
          
          {/* Progress/Status indicator */}
          <div className="mt-3 flex items-center gap-2 text-xs text-gray-500">
            <div className="flex-1 bg-gray-200 rounded-full h-2">
              <div 
                className="bg-brand-500 h-2 rounded-full transition-all duration-300"
                style={{ width: iframeLoading ? '70%' : '100%' }}
              ></div>
            </div>
            <span>{iframeLoading ? 'Đang tải kết quả...' : 'Hoàn thành'}</span>
          </div>
        </div>

        {/* Custom Result Iframe Container */}
        <div className="bg-white rounded-lg shadow-md flex-1 relative overflow-hidden">
          {/* Loading overlay */}
          {iframeLoading && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-50 z-10">
              <div className="text-center">
                <div className="w-8 h-8 border-2 border-brand-500 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
                <p className="text-gray-600">Đang tải kết quả...</p>
                <p className="text-xs text-gray-500 mt-1">Đang xử lý dữ liệu form...</p>
              </div>
            </div>
          )}
          
          {/* Custom result iframe */}
          <iframe
            id="custom-result-iframe"
            src={customResultUrl}
            className="w-full h-full border-0"
            onLoad={handleIframeLoad}
            sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
            title="Custom Result"
            style={{ minHeight: '500px' }}
          />
          
          {/* Processing overlay */}
          {processing && (
            <div className="absolute inset-0 bg-black bg-opacity-30 flex items-center justify-center z-20">
              <div className="bg-white rounded-lg p-6 text-center shadow-xl">
                <div className="w-12 h-12 border-4 border-brand-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                <h3 className="text-lg font-semibold text-gray-800 mb-2">Đang xử lý yêu cầu</h3>
                <p className="text-gray-600">Vui lòng đợi trong giây lát...</p>
              </div>
            </div>
          )}
        </div>
        
        {/* Footer info */}
        <div className="mt-4 text-center text-xs text-gray-500">
                          <p>Result ID: {`result_${Date.now()}`} | Token: {token?.substring(0, 8)}...</p>
                      {resultData && (
              <p>Completion Rate: 100% | Score: A+</p>
            )}
        </div>
      </div>
    </div>
  );
};

export default ResultPageComponent;
